import React, { useState, useEffect, useCallback } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { X, ChevronUp, ChevronDown } from 'lucide-react';
import { PlayerAvatar } from '../../../atoms';

interface WarningModalProps {
  isOpen: boolean;
  player: 'A' | 'B' | null;
  playerName: string;
  playerAName: string;
  playerBName: string;
  playerAColor: string;
  playerBColor: string;
  playerAAvatar?: string;
  playerBAvatar?: string;
  onWarningAction: (player: 'A' | 'B' | null, action: string) => void;
  onClose: () => void;
}

const TOTAL_PAGES = 3;

export function WarningModal({
  isOpen,
  player,
  playerName,
  playerAColor,
  playerBColor,
  playerAAvatar,
  playerBAvatar,
  onWarningAction,
  onClose
}: WarningModalProps) {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [showHint, setShowHint] = useState(true);

  const goToPage = useCallback((page: number) => {
    if (page >= 0 && page < TOTAL_PAGES) {
      setCurrentPage(page);
      setShowHint(false);
    }
  }, []);

  // Reset to page 0 when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(0);
      setShowHint(true);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowUp':
          e.preventDefault();
          goToPage(currentPage - 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          goToPage(currentPage + 1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentPage, onClose, goToPage]);

  const swipeHandlers = useSwipeable({
    onSwipedUp: () => goToPage(currentPage + 1),
    onSwipedDown: () => goToPage(currentPage - 1),
    preventScrollOnSwipe: true,
    trackMouse: true,
    delta: 50
  });

  // Handle mouse wheel navigation
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY > 30) {
      goToPage(currentPage + 1);
    } else if (e.deltaY < -30) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  const handleAction = (action: string, actionPlayer: 'A' | 'B' | null = player) => {
    onWarningAction(actionPlayer, action);
    onClose();
  };

  if (!isOpen) return null;

  const playerColor = player === 'A' ? playerAColor : playerBColor;
  const playerAvatar = player === 'A' ? playerAAvatar : playerBAvatar;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
        onClick={onClose}
        role="presentation"
      />

      {/* Modal Container */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${t('decision')} - ${playerName}`}
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                   w-[80vw] h-[80vh]
                   bg-white rounded-2xl overflow-hidden
                   flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <span className="text-gray-900 text-lg font-semibold">{t('decision')}</span>
          <span className="flex items-center gap-2 py-1.5 px-4 font-semibold text-gray-900">
            <PlayerAvatar avatarUrl={playerAvatar} color={playerColor} size={28} ringWidth={2} alt={playerName} />
            {playerName}
          </span>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full
                       text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label={t('common.cancel')}
          >
            <X size={24} />
          </button>
        </div>

        {/* Swipe Container */}
        <div
          {...swipeHandlers}
          onWheel={handleWheel}
          className="flex-1 relative overflow-hidden bg-gray-50"
        >
          {/* Navigation Arrows */}
          <div className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-10">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 0}
              className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center
                         text-gray-600 disabled:opacity-30 hover:bg-gray-300 transition-all"
              aria-label={t('common.previous')}
            >
              <ChevronUp size={20} />
            </button>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === TOTAL_PAGES - 1}
              className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center
                         text-gray-600 disabled:opacity-30 hover:bg-gray-300 transition-all"
              aria-label={t('common.next')}
            >
              <ChevronDown size={20} />
            </button>
          </div>

          {/* Pagination Dots */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-10">
            {[0, 1, 2].map((i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                aria-current={currentPage === i ? 'page' : undefined}
                aria-label={`Page ${i + 1}`}
                className={`w-2 rounded-full transition-all duration-300 ${
                  currentPage === i
                    ? 'h-6 bg-gray-700'
                    : 'h-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Swipe Track */}
          <div
            className="h-[300%] flex flex-col transition-transform duration-300 ease-out"
            style={{ transform: `translateY(-${currentPage * 33.333}%)` }}
          >
            {/* Page 1: Decisions */}
            <div className="h-1/3 px-12 py-4 flex flex-col justify-center">
              <div className="text-gray-500 text-xs uppercase tracking-widest text-center mb-4">
                {t('decision_page_label')}
              </div>
              <div className="flex flex-col landscape:grid landscape:grid-cols-3 gap-4 flex-1 max-h-[400px]">
                <button
                  onClick={() => handleAction('nolet')}
                  className="flex-1 font-bold text-2xl landscape:text-xl text-indigo-900
                             bg-indigo-200 hover:bg-indigo-300
                             active:scale-[0.98] transition-all"
                >
                  {t('actions.decision.noLet')}
                </button>
                <button
                  onClick={() => handleAction('let')}
                  className="flex-1 font-bold text-2xl landscape:text-xl text-white
                             bg-indigo-500 hover:bg-indigo-600
                             active:scale-[0.98] transition-all"
                >
                  {t('actions.decision.let')}
                </button>
                <button
                  onClick={() => handleAction('stroke_to')}
                  className="flex-1 font-bold text-2xl landscape:text-xl text-white
                             bg-indigo-900 hover:bg-indigo-950
                             active:scale-[0.98] transition-all"
                >
                  {t('actions.decision.stroke')} TO
                </button>
              </div>
            </div>

            {/* Page 2: Conduct */}
            <div className="h-1/3 px-12 py-4 flex flex-col justify-center">
              <div className="text-gray-500 text-xs uppercase tracking-widest text-center mb-4">
                {t('conduct_page_label')}
              </div>
              <div className="flex flex-col landscape:grid landscape:grid-cols-2 gap-4 flex-1 max-h-[400px]">
                <button
                  onClick={() => handleAction('conduct_warning', null)}
                  className="flex-1 font-bold text-xl text-black
                             bg-yellow-400 hover:bg-yellow-500
                             active:scale-[0.98] transition-all"
                >
                  {t('conduct.short.warning')}
                </button>
                <button
                  onClick={() => handleAction('conduct_point', null)}
                  className="flex-1 font-bold text-xl text-white
                             bg-orange-500 hover:bg-orange-600
                             active:scale-[0.98] transition-all"
                >
                  {t('conduct.short.stroke')}
                </button>
                <button
                  onClick={() => handleAction('conduct_game', null)}
                  className="flex-1 font-bold text-xl text-white
                             bg-red-500 hover:bg-red-600
                             active:scale-[0.98] transition-all"
                >
                  {t('conduct.short.game')}
                </button>
                <button
                  onClick={() => handleAction('conduct_match', null)}
                  className="flex-1 font-bold text-xl text-white
                             bg-red-700 hover:bg-red-800
                             active:scale-[0.98] transition-all"
                >
                  {t('conduct.short.match')}
                </button>
              </div>
            </div>

            {/* Page 3: Timers & Injury */}
            <div className="h-1/3 px-12 py-4 flex flex-col justify-center">
              <div className="text-gray-500 text-xs uppercase tracking-widest text-center mb-4">
                {t('timers_page_label')}
              </div>
              <div className="flex flex-col gap-3 flex-1 max-h-[400px]">
                <div className="flex flex-col landscape:grid landscape:grid-cols-2 gap-3 flex-1">
                  <button
                    onClick={() => handleAction('timer_equipment')}
                    className="flex-1 font-bold text-lg text-white
                               bg-cyan-500 hover:bg-cyan-600
                               active:scale-[0.98] transition-all"
                  >
                    {t('timers.equipment')}
                  </button>
                  <button
                    onClick={() => handleAction('timer_injury')}
                    className="flex-1 font-bold text-lg text-white
                               bg-cyan-600 hover:bg-cyan-700
                               active:scale-[0.98] transition-all"
                  >
                    {t('timers.selfInjury')}
                  </button>
                  <button
                    onClick={() => handleAction('timer_bleeding')}
                    className="flex-1 font-bold text-lg text-white
                               bg-cyan-700 hover:bg-cyan-800
                               active:scale-[0.98] transition-all"
                  >
                    {t('timers.selfBleeding')}
                  </button>
                  <button
                    onClick={() => handleAction('timer_shared')}
                    className="flex-1 font-bold text-lg text-white
                               bg-cyan-800 hover:bg-cyan-900
                               active:scale-[0.98] transition-all"
                  >
                    {t('timers.sharedResponsibility')}
                  </button>
                </div>
                <div className="border-t border-gray-300 my-1" />
                <button
                  onClick={() => handleAction('win_game_injury', null)}
                  className="py-3 font-bold text-lg text-white
                             bg-gray-700 hover:bg-gray-800
                             active:scale-[0.98] transition-all"
                >
                  {t('win_game_injury')}
                </button>
              </div>
            </div>
          </div>

          {/* Swipe Hint */}
          {showHint && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-gray-400 text-xs flex items-center gap-1.5 animate-bounce">
              <span>↕</span>
              <span>{t('swipe_hint')}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
